import Autoplay from "embla-carousel-autoplay";
import { useReducedMotion } from "framer-motion";
import { memo, type FC, useRef, useState } from "react";

import {
  Badge,
  Box,
  Carousel,
  CarouselContent,
  CarouselItem,
  Container,
  Flex,
  IconButton,
  MsLink,
  PauseIcon,
  PlayIcon,
  StrapiBackgroundImage,
  type SuperBannerDataProps,
  type SuperBannerMessage as SuperBannerMessageProps,
  Text,
  ThemeWrapper,
  getInternalOrExternalLink,
  getThemeValue,
  hasArrayValues,
  iconMap,
  isIconKey,
} from "@ms/gatsby-theme";

import { gridBackgroundImageProps } from "@theme/utils/ui";

import type { MsComponentPropsWithoutRef } from "@theme/types/global";

export interface SuperBannerProps
  extends SuperBannerDataProps, MsComponentPropsWithoutRef<"aside"> {
  isBlog?: boolean;
}

export const SuperBannerMessage: FC<SuperBannerMessageProps & { isBlog?: boolean }> = ({
  message,
  linkLabel,
  icon,
  iconPosition,
  link,
  isBlog,
}) => {
  const Icon = icon && isIconKey(icon.key) && iconMap[icon.key];
  const to = getInternalOrExternalLink(link?.url, isBlog);

  return (
    <MsLink
      to={to ?? "/"}
      aria-label={link?.ariaLabel ?? linkLabel ?? message}
      sx={{ display: "flex", alignItems: "center", gap: "s200" }}
    >
      {Icon && iconPosition === "leading" && <Icon sx={{ width: 24, height: 24 }} />}
      <Box>
        {message && <Text variant="sansMicro02RegularPlus">{message} </Text>}
        {linkLabel && (
          <Text variant="sansMicro02MediumPlus" sx={{ textDecoration: "underline" }}>
            {linkLabel}
          </Text>
        )}
      </Box>
      {Icon && iconPosition === "trailing" && <Icon sx={{ width: 24, height: 24 }} />}
    </MsLink>
  );
};

export const SuperBannerSingle: FC<Pick<SuperBannerProps, "message" | "isBlog">> = ({
  message,
  isBlog,
}) => (
  <>
    {message?.map((m) => (
      <SuperBannerMessage key={m.id} {...m} isBlog={isBlog} />
    ))}
  </>
);

const rotationSpeedMap = {
  slow: 6000,
  med: 5000,
  fast: 4000,
};

const SuperBannerCarouselComponent: FC<
  Pick<SuperBannerProps, "message" | "rotationSpeed" | "isBlog">
> = ({ message, rotationSpeed, isBlog }) => {
  const prefersReducedMotion = useReducedMotion();
  const autoPlay = useRef(
    Autoplay({
      delay: rotationSpeedMap[rotationSpeed || "slow"] || rotationSpeedMap.slow,
      playOnInit: !prefersReducedMotion,
    }),
  );
  const [isPlaying, setIsPlaying] = useState(!prefersReducedMotion);

  return (
    <Carousel
      carouselName="Super Banner"
      opts={{ loop: true }}
      plugins={[autoPlay.current]}
      removeSkipCarouselButtons
    >
      <Flex
        sx={{
          flexDirection: "row",
          gap: "s200",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <CarouselContent>
          {message?.map((m) => (
            <CarouselItem key={m.id} sx={{ flex: "0 0 100%" }}>
              <SuperBannerMessage key={m.id} {...m} isBlog={isBlog} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <IconButton
          variant="ghost"
          onClick={() => {
            if (isPlaying) {
              autoPlay.current.stop();
              setIsPlaying(false);
            } else {
              autoPlay.current.play();
              setIsPlaying(true);
            }
          }}
          aria-label={
            isPlaying ? "Pause announcement banner carousel" : "Play announcement banner carousel"
          }
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
      </Flex>
    </Carousel>
  );
};

export const SuperBannerCarousel = memo(SuperBannerCarouselComponent);

export const SuperBanner: FC<SuperBannerProps> = ({
  isBlog,
  badgeText,
  alignment,
  bgColor,
  message,
  bgImage,
  theme,
  rotationSpeed,
  sx,
  ...props
}) => {
  const isCarousel = hasArrayValues(message) && message.length > 1;

  return (
    <Box
      as="aside"
      sx={{
        height: (t) => getThemeValue(t, "sizes.superBannerHeight") || 80,
        width: "full",
        bg: bgColor ? `#${bgColor.replace("#", "")}` : "goldPrimary",
        position: "fixed",
        overflow: "hidden",
        zIndex: 10,
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...sx,
      }}
      {...props}
    >
      <Box sx={{ display: "grid", width: "full", height: "full" }}>
        {bgImage && (
          <StrapiBackgroundImage
            images={bgImage}
            sx={{ gridArea: "1 / 1", width: "full" }}
            {...gridBackgroundImageProps}
          />
        )}
        <Container
          variant="wide"
          sx={{
            gridArea: "1 / 1",
            position: "relative",
            display: "flex",
            alignItems: "center",
            height: "full",
            width: "full",
            justifyContent: alignment === "left" ? "flex-start" : "center",
            gap: "s300",
          }}
        >
          <ThemeWrapper theme={theme}>
            {badgeText && (
              <Badge variant="neutral" sx={{ display: ["none", null, null, "block"] }}>
                {badgeText}
              </Badge>
            )}
            {isCarousel ? (
              <SuperBannerCarousel
                message={message}
                rotationSpeed={rotationSpeed}
                isBlog={isBlog}
              />
            ) : (
              <SuperBannerSingle message={message} isBlog={isBlog} />
            )}
          </ThemeWrapper>
        </Container>
      </Box>
    </Box>
  );
};
